import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import Marquee from 'react-fast-marquee';
import { Link } from 'react-router-dom';
import {
  BookOpen, Users, GraduationCap, ClipboardCheck,
  UserCheck, BookMarked, TrendingUp, Target,
  Phone, ArrowRight, Star, Monitor, HelpCircle, Compass, MapPin
} from 'lucide-react';
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger
} from '@/components/ui/accordion';
import LeadForm from '@/components/LeadForm';

const WA = 'https://wa.me/919930010800?text=Hi%20Planet%20Gyaan!%20I%27d%20like%20to%20know%20more%20about%20your%20classes.';
const TEL = 'tel:+919930010800';
const LOGO = 'https://customer-assets.emergentagent.com/job_planet-gyaan-edu/artifacts/no8w3qt3_Untitled%20design-73.png';

const WaIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

/* ---- Animated Counter ---- */
const CountUp = ({ end, suffix = '' }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);
  const numEnd = parseInt(end);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started.current) {
          started.current = true;
          let frame = 0;
          const totalFrames = 90;
          const timer = setInterval(() => {
            frame++;
            setCount(Math.floor(numEnd * Math.min(frame / totalFrames, 1)));
            if (frame >= totalFrames) clearInterval(timer);
          }, 1000 / 60);
        }
      },
      { threshold: 0.4 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [numEnd]);

  return <span ref={ref}>{count}{suffix}</span>;
};

/* ---- Rotating Word Animation ---- */
const RotatingWord = ({ words }) => {
  const [index, setIndex] = useState(0);
  useEffect(() => {
    const timer = setInterval(() => setIndex(i => (i + 1) % words.length), 2500);
    return () => clearInterval(timer);
  }, [words.length]);
  return (
    <span className="inline-block relative">
      <AnimatePresence mode="wait">
        <motion.span
          key={words[index]}
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -30, opacity: 0 }}
          transition={{ duration: 0.4 }}
          className="inline-block text-[#F2C402]"
        >
          {words[index]}
        </motion.span>
      </AnimatePresence>
    </span>
  );
};

const stats = [
  { value: 2013, suffix: '', label: 'Established' },
  { value: 500, suffix: '+', label: 'Students Taught' },
  { value: 95, suffix: '%+', label: 'Score Achievers' },
  { value: 12, suffix: '+', label: 'Years of Excellence' },
];

const features = [
  { icon: BookOpen, title: 'Concept-Based Learning', desc: 'Deep understanding over rote memorization. We build strong fundamentals that last.' },
  { icon: Users, title: 'Small Batch Sizes', desc: 'Maximum 8-10 students per batch for focused, interactive learning sessions.' },
  { icon: GraduationCap, title: 'Experienced Faculty', desc: 'Qualified teachers with years of CBSE & competitive exam expertise.' },
  { icon: ClipboardCheck, title: 'Regular Assessments', desc: 'Weekly tests and detailed performance tracking to measure progress.' },
  { icon: UserCheck, title: 'Personal Attention', desc: "Individual focus on each student's strengths and areas of improvement." },
  { icon: BookMarked, title: 'Custom Study Material', desc: 'Structured and curated materials aligned with the CBSE curriculum.' },
  { icon: TrendingUp, title: 'Performance Tracking', desc: 'Continuous monitoring with detailed progress reports for parents.' },
  { icon: Target, title: 'JEE & NEET Prep', desc: 'Specialized coaching for competitive entrance examinations alongside CBSE.' },
];

const testimonials = [
  { name: 'Arjun S.', grade: 'Grade 12', text: 'Planet Gyaan transformed my understanding of Physics. I scored 97% in my boards!', score: '97%' },
  { name: 'Priya M.', grade: 'Grade 11', text: 'Small batch sizes mean I get all my doubts cleared. Best decision for JEE prep.', score: '95%' },
  { name: 'Rahul K.', grade: 'Grade 10', text: 'Improved from 65% to 92% in Math in just 6 months. The personalized attention works.', score: '92%' },
  { name: 'Sneha T.', grade: 'Grade 12', text: 'NEET preparation here is structured and thorough. Faculty really cares about each student.', score: '96%' },
  { name: 'Aditya P.', grade: 'Grade 9', text: "I was scared of Math, now it's my favorite! Teachers make concepts so easy to understand.", score: '94%' },
  { name: 'Kavya R.', grade: 'Grade 11', text: 'Career counselling helped me choose between Engineering and Medicine. Clear path now!', score: '93%' },
  { name: 'Rohan D.', grade: 'Grade 10', text: 'The 1:1 doubt clearing sessions are amazing. I can ask anything without hesitation.', score: '91%' },
  { name: 'Meera S.', grade: 'Grade 12', text: 'Scored 95%+ in both Math and Science. The structured approach really works!', score: '95%' },
];

const faqs = [
  { q: 'What grades do you teach?', a: 'We teach students from Grade 8 to 12 (CBSE Board) and also prepare for competitive exams like JEE, NEET and other entrance tests.' },
  { q: 'Which subjects are offered?', a: 'We specialize in Mathematics and Science (Physics, Chemistry, Biology) for CBSE students.' },
  { q: 'What is the batch size?', a: 'We maintain small batch sizes of 8-10 students to ensure personalized attention and better learning.' },
  { q: 'Do you offer online classes?', a: 'Yes! We offer both virtual (online) and offline tuitions. You can choose the mode that suits you best.' },
  { q: 'How can I enroll?', a: 'Simply WhatsApp us at 9930010800 or visit our center at Santacruz West, Mumbai. We\'ll schedule a free consultation.' },
  { q: 'Do you provide trial classes?', a: 'Yes, we offer a free demo class so you can experience our teaching methodology before enrolling.' },
  { q: 'What about JEE/NEET preparation?', a: 'We provide focused JEE and NEET preparation alongside the regular CBSE curriculum with special emphasis on problem-solving.' },
  { q: 'How do you track student progress?', a: 'We conduct regular tests, provide detailed performance reports, and maintain continuous tracking to monitor each student.' },
];

const services = [
  { title: 'Regular Batch', desc: 'Virtual + Offline tuitions for CBSE Grades 8-12. Regular & Private batches available with structured schedules.', icon: Monitor, link: '/#services', color: '#F2C402' },
  { title: '1:1 Doubt Clearing', desc: 'Private doubt clearing sessions. Get personalized attention for tough concepts at your own pace.', icon: HelpCircle, link: '/doubt-clearing', color: '#E9811F' },
  { title: 'Career Counselling', desc: '1:1 career guidance sessions to help students navigate the right future path with confidence.', icon: Compass, link: '/career-counselling', color: '#F2C402' },
];

const Home = () => {
  const [activeFeature, setActiveFeature] = useState(0);

  return (
    <>
      <Helmet>
        <title>Planet Gyaan - Math & Science Coaching | CBSE Grades 8-12 | Santacruz Mumbai</title>
        <meta name="description" content="Planet Gyaan offers expert Math & Science coaching for CBSE students in Grades 8-12. Small batches, experienced faculty, JEE & NEET preparation. Santacruz West, Mumbai. Call 9930010800." />
        <meta name="keywords" content="CBSE coaching Santacruz, math tuition Mumbai, science tuition Santacruz West, JEE preparation, NEET preparation, Grade 8-12 coaching, Planet Gyaan" />
        <meta property="og:title" content="Planet Gyaan - Math & Science Coaching | Santacruz Mumbai" />
        <meta property="og:description" content="Expert CBSE coaching for Grades 8-12. Small batches, JEE & NEET prep. Since 2013." />
        <meta property="og:image" content={LOGO} />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://planetgyaan.com/" />
      </Helmet>

      {/* ===== HERO (Interactive) ===== */}
      <section data-testid="hero-section" className="relative min-h-screen bg-[#18181B] flex items-center overflow-hidden noise-bg">
        {/* Floating formula badges */}
        {['E=mc\u00B2', '\u0394x\u00B7dx', 'F=ma', 'H\u2082O', '\u03C0r\u00B2', 'sin\u03B8'].map((f, i) => (
          <motion.span
            key={i}
            className="absolute text-[#F2C402]/10 font-mono text-sm md:text-base font-bold select-none pointer-events-none"
            style={{ left: `${15 + (i * 14) % 80}%`, top: `${20 + (i * 17) % 60}%` }}
            animate={{ y: [0, -15, 0], opacity: [0.08, 0.15, 0.08] }}
            transition={{ duration: 3 + i * 0.5, repeat: Infinity, ease: 'easeInOut', delay: i * 0.4 }}
          >
            {f}
          </motion.span>
        ))}
        <div className="relative z-10 container mx-auto px-6 lg:px-16 pt-28 pb-16">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <motion.span
                initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
                className="inline-block text-[#E9811F] font-bold uppercase tracking-[0.2em] text-xs md:text-sm mb-6 font-body"
              >
                Since 2013 &bull; Santacruz, Mumbai
              </motion.span>
              <motion.h1
                initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }}
                className="text-5xl sm:text-6xl lg:text-7xl font-black text-[#F2C402] tracking-tighter leading-[0.9]"
              >
                Master<br/>
                <RotatingWord words={['Mathematics', 'Physics', 'Chemistry', 'Biology']} /> &<br/>
                <span className="text-white">Excel</span>
              </motion.h1>
              <motion.p
                initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.2 }}
                className="text-white/60 text-base md:text-lg mt-6 max-w-md font-body leading-relaxed"
              >
                CBSE Grades 8-12 coaching with JEE & NEET preparation. Concept-based learning, small batches, real results.
              </motion.p>
              <motion.div
                initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.3 }}
                className="flex flex-wrap gap-4 mt-8"
              >
                <a href={WA} target="_blank" rel="noopener noreferrer" data-testid="hero-whatsapp-cta"
                  className="flex items-center gap-2 bg-[#25D366] text-white px-6 py-3.5 rounded-xl font-bold text-sm hover:brightness-110 hover:scale-105 transition-all">
                  <WaIcon className="w-5 h-5" /> WhatsApp Us
                </a>
                <a href={TEL} data-testid="hero-call-cta"
                  className="flex items-center gap-2 bg-[#E9811F] text-white px-6 py-3.5 rounded-xl font-bold text-sm hover:brightness-110 hover:scale-105 transition-all">
                  <Phone className="w-4 h-4" /> Call Now
                </a>
              </motion.div>
              {/* Trust badges */}
              <motion.div
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }}
                className="flex items-center gap-6 mt-10 text-white/30 text-xs font-body"
              >
                <span className="flex items-center gap-1.5"><Star className="w-3.5 h-3.5 fill-[#F2C402] text-[#F2C402]" /> 95%+ Results</span>
                <span className="flex items-center gap-1.5"><Users className="w-3.5 h-3.5 text-[#F2C402]" /> 500+ Students</span>
                <span className="flex items-center gap-1.5"><GraduationCap className="w-3.5 h-3.5 text-[#F2C402]" /> Since 2013</span>
              </motion.div>
            </div>
            {/* Interactive right side - Subject cards */}
            <motion.div
              initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8, delay: 0.2 }}
              className="hidden lg:grid grid-cols-2 gap-4"
            >
              {[
                { subject: 'Mathematics', topics: 'Algebra, Calculus, Geometry', icon: '\u03A3', color: '#F2C402' },
                { subject: 'Physics', topics: 'Mechanics, Optics, Waves', icon: '\u26A1', color: '#E9811F' },
                { subject: 'Chemistry', topics: 'Organic, Inorganic, Physical', icon: '\u2697', color: '#F2C402' },
                { subject: 'Biology', topics: 'Botany, Zoology, Genetics', icon: '\uD83E\uDDEC', color: '#E9811F' },
              ].map((s, i) => (
                <motion.div
                  key={i}
                  whileHover={{ scale: 1.05, y: -5 }}
                  transition={{ type: 'spring', stiffness: 300 }}
                  className="glass-card rounded-2xl p-6 cursor-pointer group"
                  data-testid={`hero-subject-${i}`}
                >
                  <span className="text-3xl mb-3 block">{s.icon}</span>
                  <h3 className="font-sans font-bold text-white text-base group-hover:text-[#F2C402] transition-colors">{s.subject}</h3>
                  <p className="text-white/40 text-xs font-body mt-1">{s.topics}</p>
                  <div className="w-full h-1 rounded-full bg-white/10 mt-3 overflow-hidden">
                    <motion.div
                      className="h-full rounded-full"
                      style={{ backgroundColor: s.color }}
                      initial={{ width: 0 }}
                      animate={{ width: '100%' }}
                      transition={{ duration: 1.5, delay: 0.5 + i * 0.2 }}
                    />
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* ===== MARQUEE ===== */}
      <div data-testid="marquee-section" className="border-y-2 border-[#121212]">
        <Marquee speed={60} className="bg-[#F2C402] py-4">
          {['CBSE Board', 'JEE Preparation', 'NEET Preparation', 'Grade 8-12', 'Mathematics', 'Science', 'Small Batches', 'Since 2013'].map((item, i) => (
            <span key={i} className="mx-8 text-[#0A0A0A] font-sans font-bold text-sm md:text-base tracking-wide flex items-center gap-3">
              <span className="w-1.5 h-1.5 rounded-full bg-[#0A0A0A]" /> {item}
            </span>
          ))}
        </Marquee>
      </div>

      {/* ===== STATS (Interactive Counters) ===== */}
      <section data-testid="stats-section" className="py-20 md:py-28 bg-white">
        <div className="container mx-auto px-6 lg:px-16">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {stats.map((stat, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ delay: i * 0.1, duration: 0.5 }}
                className="brutalist-card rounded-2xl p-6 md:p-8 bg-white text-center group hover:bg-[#F2C402] transition-colors duration-300"
              >
                <span className="block text-3xl md:text-4xl font-black text-[#F2C402] font-sans group-hover:text-[#0A0A0A] transition-colors">
                  <CountUp end={stat.value} suffix={stat.suffix} />
                </span>
                <span className="block mt-2 text-[#52525B] text-sm font-body group-hover:text-[#0A0A0A]/70 transition-colors">{stat.label}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== STUDENT ACHIEVERS CAROUSEL ===== */}
      <section data-testid="achievers-carousel" className="py-16 md:py-20 bg-[#FAFAFA]">
        <div className="container mx-auto px-6 lg:px-16">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-10">
            <span className="text-[#E9811F] font-bold uppercase tracking-[0.15em] text-xs font-body">Our Pride</span>
            <h2 className="text-3xl md:text-4xl font-black text-[#121212] tracking-tight mt-2">
              Our <span className="text-[#F2C402]">Star Achievers</span>
            </h2>
          </motion.div>
        </div>
        <Marquee speed={40} pauseOnHover className="py-4">
          {[
            { name: 'Jaideep Sahoo', pct: '99%', img: 'https://customer-assets.emergentagent.com/job_planet-gyaan-edu/artifacts/hzbvo16h_Jaideep%20Sahoo%2099%25.jpeg' },
            { name: 'Nipun Prabhu', pct: '98.9%', img: 'https://customer-assets.emergentagent.com/job_planet-gyaan-edu/artifacts/ma94u0mj_Nipun%20prabhu%2C%2098.9%25.jpeg' },
            { name: 'Neelabh Upadhyay', pct: '98.2%', img: 'https://customer-assets.emergentagent.com/job_planet-gyaan-edu/artifacts/skog0wlv_Neelabh%20Upadhyay%20-%2098.2%25.jpeg' },
            { name: 'Aditya Pillai', pct: '98%', img: 'https://customer-assets.emergentagent.com/job_planet-gyaan-edu/artifacts/81mpy3l0_Aditya%20Pillai%2098%25.jpeg' },
            { name: 'Khewan Agrawal', pct: '97.6%', img: 'https://customer-assets.emergentagent.com/job_planet-gyaan-edu/artifacts/c6ikcddd_Khewan%20Agrawal%20-%2097.6%25%2C.jpeg' },
            { name: 'Krish Dhume', pct: '97.4%', img: 'https://customer-assets.emergentagent.com/job_planet-gyaan-edu/artifacts/oc3argmv_Krish%20Dhume%2097.4%25.jpeg' },
            { name: 'Charitra Jain', pct: '97.2%', img: 'https://customer-assets.emergentagent.com/job_planet-gyaan-edu/artifacts/n3112c43_Charitra%20Jain%20-%2097.2%25.jpeg' },
            { name: 'Anushka Rao', pct: '97%', img: 'https://customer-assets.emergentagent.com/job_planet-gyaan-edu/artifacts/wfpd4tcu_Anushka%20Rao%20-%2097%25.jpeg' },
            { name: 'Durva Kolhal', pct: '96.8%', img: 'https://customer-assets.emergentagent.com/job_planet-gyaan-edu/artifacts/dcsfa48u_Durva%20Kolhal%2096.8%25.jpeg' },
            { name: 'Akshat Menon', pct: '96.8%', img: 'https://customer-assets.emergentagent.com/job_planet-gyaan-edu/artifacts/3mxesotx_Akshat%20Menon%2096.8%25.jpeg' },
            { name: 'Daksh Joshi', pct: '94%', img: 'https://customer-assets.emergentagent.com/job_planet-gyaan-edu/artifacts/5f6rms46_Daksh%20Joshi%2094%25.jpeg' },
          ].map((s, i) => (
            <div key={i} className="mx-4 flex-shrink-0">
              <div className="w-48 rounded-2xl border-2 border-[#121212] shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] bg-white overflow-hidden hover:shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] hover:-translate-y-1 transition-all">
                <div className="h-48 overflow-hidden">
                  <img src={s.img} alt={s.name} className="w-full h-full object-cover object-top" />
                </div>
                <div className="p-4 text-center">
                  <p className="font-sans font-bold text-[#121212] text-sm">{s.name}</p>
                  <p className="text-[#F2C402] font-black text-xl font-sans mt-1">{s.pct}</p>
                  <p className="text-[#52525B] text-xs font-body">CBSE Board</p>
                </div>
              </div>
            </div>
          ))}
        </Marquee>
      </section>

      {/* ===== ABOUT ===== */}
      <section data-testid="about-section" className="py-20 md:py-28 bg-[#FAFAFA]">
        <div className="container mx-auto px-6 lg:px-16">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }} transition={{ duration: 0.6 }}
            >
              {/* Founders with actual photos */}
              <div className="rounded-2xl border-2 border-[#121212] shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] bg-[#FAFAFA] overflow-hidden">
                <div className="bg-[#F2C402]/10 p-8 flex items-center justify-center gap-8">
                  <div className="text-center">
                    <div className="w-28 h-28 rounded-full border-4 border-[#F2C402] shadow-lg overflow-hidden mx-auto">
                      <img src="https://customer-assets.emergentagent.com/job_planet-gyaan-edu/artifacts/t0p09omw_Aaravind%20Achari%20cofounder.png" alt="Aravind Achari" className="w-full h-full object-cover" />
                    </div>
                    <p className="font-sans font-bold text-[#121212] text-sm mt-3">Aravind Achari</p>
                    <p className="text-[#52525B] text-xs font-body">Co-Founder</p>
                  </div>
                  <div className="text-center">
                    <div className="w-28 h-28 rounded-full border-4 border-[#E9811F] shadow-lg overflow-hidden mx-auto">
                      <img src="https://customer-assets.emergentagent.com/job_planet-gyaan-edu/artifacts/4pw0qpdu_Priyanka.png" alt="Priyanka Achari" className="w-full h-full object-cover" />
                    </div>
                    <p className="font-sans font-bold text-[#121212] text-sm mt-3">Priyanka Achari</p>
                    <p className="text-[#52525B] text-xs font-body">Co-Founder</p>
                  </div>
                </div>
                <div className="p-5 text-center">
                  <h3 className="font-sans font-bold text-[#121212] text-lg">Meet the Founders</h3>
                  <p className="text-[#52525B] text-sm font-body mt-2 leading-relaxed">
                    With a shared passion for education, they founded Planet Gyaan in 2013 with just two students and a vision to make quality Math & Science coaching accessible to every CBSE student.
                  </p>
                </div>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.1 }}
            >
              <span className="text-[#E9811F] font-bold uppercase tracking-[0.15em] text-xs font-body">Our Story</span>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-[#121212] tracking-tight mt-3">
                From 2 Students to a <span className="text-[#F2C402]">Trusted Learning Hub</span>
              </h2>
              <p className="text-[#52525B] text-base md:text-lg mt-6 font-body leading-relaxed">
                Planet Gyaan is a well-established coaching institute founded in 2013 by <strong>Aravind Achari & Priyanka Achari</strong>, dedicated to providing high-quality education in Mathematics and Science. Starting with just two students, it has grown into a trusted learning hub helping students from Grade 8 to 12 (CBSE Board), along with preparation for competitive exams like JEE, NEET and other entrance tests.
              </p>
              <p className="text-[#52525B] text-base md:text-lg mt-4 font-body leading-relaxed">
                The institute focuses on concept-based learning through small batch sizes, experienced and qualified teachers, regular tests and personalized attention. With strong results, including students scoring above 95%, Planet Gyaan aims to build confidence, clarity and long-term success in every student.
              </p>
              <div className="flex flex-wrap gap-4 mt-8">
                <a href={WA} target="_blank" rel="noopener noreferrer" data-testid="about-whatsapp-cta"
                  className="flex items-center gap-2 bg-[#F2C402] text-[#0A0A0A] px-6 py-3 rounded-xl font-bold text-sm hover:bg-[#E9811F] hover:text-white transition-all">
                  Book a Free Demo <ArrowRight className="w-4 h-4" />
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ===== SERVICES (Light BG) ===== */}
      <section id="services" data-testid="services-section" className="py-20 md:py-28 bg-white">
        <div className="container mx-auto px-6 lg:px-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }} className="text-center mb-16"
          >
            <span className="text-[#E9811F] font-bold uppercase tracking-[0.15em] text-xs font-body">What We Offer</span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-[#121212] tracking-tight mt-3">
              Our <span className="text-[#F2C402]">Services</span>
            </h2>
          </motion.div>
          <div className="grid md:grid-cols-3 gap-6">
            {services.map((s, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ delay: i * 0.1, duration: 0.5 }}
              >
                <Link
                  to={s.link}
                  data-testid={`service-card-${i}`}
                  className="block brutalist-card rounded-2xl p-8 md:p-10 bg-white hover:bg-[#FAFAFA] transition-all group"
                >
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-6" style={{ backgroundColor: s.color }}>
                    <s.icon className="w-6 h-6 text-[#0A0A0A]" />
                  </div>
                  <h3 className="text-xl font-bold text-[#121212] font-sans mb-3">{s.title}</h3>
                  <p className="text-[#52525B] text-sm font-body leading-relaxed">{s.desc}</p>
                  <span className="inline-flex items-center gap-1 text-[#E9811F] text-sm font-bold mt-5 group-hover:gap-2 transition-all font-body">
                    Learn More <ArrowRight className="w-4 h-4" />
                  </span>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== 2026 CBSE BOARD RESULTS ===== */}
      <section data-testid="results-section" className="py-20 md:py-28 bg-[#FAFAFA]">
        <div className="container mx-auto px-6 lg:px-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }} className="text-center mb-12"
          >
            <span className="text-[#E9811F] font-bold uppercase tracking-[0.15em] text-xs font-body">Proven Results</span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-[#121212] tracking-tight mt-3">
              Our <span className="text-[#F2C402]">Achievers</span>
            </h2>
            <p className="text-[#52525B] text-base font-body mt-3">CBSE Board Results - Our students, our pride</p>
          </motion.div>
          <div className="max-w-4xl mx-auto brutalist-card rounded-2xl overflow-hidden bg-white">
            <div className="grid grid-cols-5 bg-[#121212] text-white text-xs md:text-sm font-sans font-bold">
              <div className="p-3 md:p-4 col-span-2">Student Name</div>
              <div className="p-3 md:p-4 text-center">Math</div>
              <div className="p-3 md:p-4 text-center">Science</div>
              <div className="p-3 md:p-4 text-center bg-[#F2C402] text-[#0A0A0A]">%</div>
            </div>
            {[
              { name: 'Khewan Agrawal', math: '94', science: '98', pct: '97.6%' },
              { name: 'Vikram Mishra', math: '95', science: '96', pct: '97.6%' },
              { name: 'Charitra Jain', math: '-', science: '92', pct: '97.2%' },
              { name: 'Durva Kolhal', math: '-', science: '91', pct: '96.8%' },
              { name: 'Ira Talaulikar', math: '90', science: '97', pct: '96.4%' },
              { name: 'Kavya Shah', math: '83', science: '94', pct: '96.4%' },
              { name: 'Arjun Gupta', math: '93', science: '92', pct: '96.2%' },
              { name: 'Shravani Aute', math: '85', science: '93', pct: '96.2%' },
              { name: 'Mohak Dang', math: '-', science: '97', pct: '95.8%' },
              { name: 'Saicharan G.', math: '-', science: '-', pct: '95.8%' },
              { name: 'Ishita Sharma', math: '85', science: '98', pct: '95%' },
              { name: 'Sanvi Kulkarni', math: '86', science: '92', pct: '95%' },
            ].map((s, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -10 }} whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }} transition={{ delay: i * 0.03 }}
                className={`grid grid-cols-5 text-sm font-body ${i % 2 === 0 ? 'bg-white' : 'bg-[#FAFAFA]'} border-b border-gray-100 hover:bg-[#F2C402]/5 transition-colors`}
              >
                <div className="p-3 md:p-4 col-span-2 font-medium text-[#121212]">{s.name}</div>
                <div className="p-3 md:p-4 text-center text-[#52525B]">{s.math}</div>
                <div className="p-3 md:p-4 text-center text-[#52525B]">{s.science}</div>
                <div className="p-3 md:p-4 text-center font-black text-[#F2C402] font-sans">{s.pct}</div>
              </motion.div>
            ))}
          </div>
          <p className="text-center text-[#52525B] text-xs font-body mt-4">Results in Math & Science subjects only. Overall percentage shown.</p>
        </div>
      </section>

      {/* ===== TESTIMONIALS ===== */}
      <section data-testid="testimonials-section" className="py-20 md:py-28 bg-white">
        <div className="container mx-auto px-6 lg:px-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }} className="text-center mb-16"
          >
            <span className="text-[#E9811F] font-bold uppercase tracking-[0.15em] text-xs font-body">Testimonials</span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-[#121212] tracking-tight mt-3">
              What <span className="text-[#F2C402]">Students Say</span>
            </h2>
          </motion.div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {testimonials.map((t, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ delay: i * 0.05, duration: 0.4 }}
                data-testid={`testimonial-card-${i}`}
                className="bg-[#FAFAFA] rounded-2xl p-6 border-2 border-[#121212] shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover:shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] hover:-translate-x-[1px] hover:-translate-y-[1px] transition-all"
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, j) => <Star key={j} className="w-4 h-4 fill-[#F2C402] text-[#F2C402]" />)}
                </div>
                <p className="text-[#121212] text-sm font-body leading-relaxed mb-4">"{t.text}"</p>
                <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                  <div>
                    <span className="block font-sans font-bold text-[#121212] text-sm">{t.name}</span>
                    <span className="block text-[#52525B] text-xs font-body">{t.grade}</span>
                  </div>
                  <span className="text-[#F2C402] font-black text-lg font-sans">{t.score}</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== FAQ ===== */}
      <section data-testid="faq-section" className="py-20 md:py-28 bg-[#FAFAFA]">
        <div className="container mx-auto px-6 lg:px-16 max-w-3xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }} className="text-center mb-16"
          >
            <span className="text-[#E9811F] font-bold uppercase tracking-[0.15em] text-xs font-body">FAQ</span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-[#121212] tracking-tight mt-3">
              Got <span className="text-[#F2C402]">Questions?</span>
            </h2>
          </motion.div>
          <Accordion type="single" collapsible className="space-y-3" data-testid="faq-accordion">
            {faqs.map((faq, i) => (
              <AccordionItem
                key={i} value={`faq-${i}`}
                data-testid={`faq-item-${i}`}
                className="bg-white rounded-xl border-2 border-[#121212] px-6 overflow-hidden"
              >
                <AccordionTrigger className="text-[#121212] font-sans font-bold text-sm md:text-base hover:no-underline py-5">
                  {faq.q}
                </AccordionTrigger>
                <AccordionContent className="text-[#52525B] font-body text-sm leading-relaxed">
                  {faq.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* ===== CTA + LEAD FORM ===== */}
      <section data-testid="cta-section" className="py-16 md:py-24 bg-[#F2C402] border-y-2 border-[#121212]">
        <div className="container mx-auto px-6 lg:px-16">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="text-center lg:text-left">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-[#0A0A0A] tracking-tight">
                Ready to Excel?
              </h2>
              <p className="text-[#0A0A0A]/70 text-base md:text-lg mt-4 font-body max-w-md">
                Join Planet Gyaan today. Contact us for pricing and batch details.
              </p>
              <div className="flex flex-wrap gap-4 mt-8 justify-center lg:justify-start">
                <a href={WA} target="_blank" rel="noopener noreferrer" data-testid="cta-whatsapp"
                  className="flex items-center gap-2 bg-[#0A0A0A] text-white px-8 py-4 rounded-xl font-bold text-sm hover:bg-[#121212] transition-all shadow-[4px_4px_0px_0px_rgba(0,0,0,0.3)]">
                  <WaIcon className="w-5 h-5" /> WhatsApp Us
                </a>
                <a href={TEL} data-testid="cta-call"
                  className="flex items-center gap-2 bg-white text-[#0A0A0A] px-8 py-4 rounded-xl font-bold text-sm hover:bg-[#FAFAFA] transition-all border-2 border-[#121212] shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
                  <Phone className="w-4 h-4" /> Call Now
                </a>
              </div>
              <div className="flex items-center gap-2 mt-6 text-[#0A0A0A]/60 text-sm font-body justify-center lg:justify-start">
                <MapPin className="w-4 h-4" />
                <a href="https://www.google.com/maps/search/?api=1&query=Planet+Gyaan+Tirupati+Shopping+Center+Santacruz+West+Mumbai"
                  target="_blank" rel="noopener noreferrer" data-testid="cta-directions"
                  className="underline hover:text-[#0A0A0A] transition-colors">
                  Get Directions to Our Center
                </a>
              </div>
              <a href="tel:+919930030600" className="flex items-center gap-2 mt-2 text-[#0A0A0A]/60 text-sm font-body justify-center lg:justify-start hover:text-[#0A0A0A] transition-colors">
                <Phone className="w-4 h-4" /> Also call: 9930030600
              </a>
            </div>
            <div>
              <LeadForm />
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Home;
